import '../styles/content.scss';
import '../styles/content-custom.scss';
import { useContext, useEffect, useMemo, useRef } from 'react';
import { EditorContent } from '@tiptap/react';
import classNames from 'classnames';
import DocumentContext from '../contexts/DocumentContext.context';
import LinkNodeBubbleMenu from '../tiptap/nodes/link/LinkNodeBubbleMenu';
import TableNodeBubbleMenu from '../tiptap/nodes/table/TableNodeBubbleMenu';
import InlineBlockNodeBubbleMenu from '../tiptap/nodes/inlineBlock/InlineBlockNodeBubbleMenu';
import LiteratureCitationBubbleMenu from '../tiptap/nodes/literatureCitation/LiteratureCitationBubbleMenu';
import getDocumentConfig from '../helpers/getDocumentConfig.helper';
import { isVersionDetached, isVersionBound } from '../helpers/isVersionBound.helper';
import { isBoundDocument, isSelectionInField } from '../tiptap/extensions/TemplateLocking';
import Changes from './Changes';
import { ToastContext } from '@shared/contexts';
import { StatusLabel, CustomScrollbar } from '@shared/components';
import { layoutTransition } from '@shared/constants/layoutTransition.constants';
import { SignInContext } from '@modules/users';
import { useItemScreenStore } from '@modules/items';
import { useCanEdit } from '@modules/access';
import { useGroundedQaCitationLocator } from '@modules/ai/groundedQa/api';
import { TERMINOLOGY } from '@shared/i18n';
import { EditorCommentsContainer } from '@modules/comments';
import { useSearchParams } from '@shared/hooks';
import { uniqueIds } from '../helpers/scrollContent/scrollTargets';
import { useLocation } from 'react-router-dom';
import useScrollContent from '../hooks/useScrollContent.hook';
import useHandleEditorContentClick from '../hooks/useEditorContentClick';
import useHasLandscapeSections from '../hooks/useHasLandscapeSections.hook';
import { makeStyles, tokens } from '@fluentui/react-components';
import ImageNodeBubbleMenu from '../tiptap/nodes/image/ImageNodeBubbleMenu';
import DropdownFieldBubbleMenu from '../tiptap/nodes/templateField/DropdownFieldBubbleMenu';
import FieldSettingsBubbleMenu from '../tiptap/nodes/templateField/FieldSettingsBubbleMenu';
import FieldDescriptionTooltip from '../tiptap/nodes/templateField/FieldDescriptionTooltip';
import FormFieldGutter from './formFields/FormFieldGutter';
import { usesFormFields } from '../helpers/formFields.helper';
import StaticElements from '../largeDocument/static/StaticElements';
import { isPartSwap, whenSwapDone } from '../largeDocument';
// Direct file import: through the barrel this hook's own import of DocumentContext closes a cycle.
import useStaticRenderContext from '../largeDocument/static/useStaticRenderContext.hook';

const TOAST_MAX_CLICK_COUNT = 2;
const TOAST_TIMEOUT = 6000;

const useStyles = makeStyles({
  toast: {
    backgroundColor: tokens.colorBrandBackground2,
    '& .fui-ToastTitle': {
      color: 'black',
    },
    '& .fui-ToastTitle__media': {
      color: 'black',
    },
  },
});

type TProps = {
  // The label of the page's button that starts editing, when the page decides it rather than the document's draft.
  editActionLabel?: string;
};

const Content = ({ editActionLabel }: TProps) => {
  const { organization } = useContext(SignInContext);
  const { doc, replica, version, editor, refContentNode, contentStatus, isEdit, requestEdit, permissions } =
    useContext(DocumentContext);
  const { canEdit } = useCanEdit(permissions.accessRight);
  useStaticRenderContext();
  const { viewChanges } = useItemScreenStore();
  const { show } = useContext(ToastContext);
  const refEditorContent = useRef<HTMLDivElement>(null);
  const refLastScrollKey = useRef<string | null>(null);
  const refLastNavigationKey = useRef<string | null>(null);
  const refContentReady = useRef(false);
  const { searchParams } = useSearchParams();
  const location = useLocation();
  const navigationKey = location.key;
  // Only a link the user clicked counts as asking again. Writing any query parameter elsewhere -
  // closing the assistant, opening a dialog - also creates a new key and must not move the page.
  const askedByClick = Boolean((location.state as { scrollRequest?: boolean } | null)?.scrollRequest);
  const elemToScroll = searchParams.get('elem');
  const citationRunId = searchParams.get('qaRun');
  const citationIndex = searchParams.get('qaCitation');
  const locatorEnabled = Boolean(citationRunId) && citationIndex !== null;
  const locatorQuery = useGroundedQaCitationLocator(citationRunId, citationIndex);
  const locatorElementIds = useMemo(
    () => (locatorQuery.data ? uniqueIds([locatorQuery.data.elementId, ...locatorQuery.data.elementIds]) : []),
    [locatorQuery.data]
  );
  const citationLocatorReady = !locatorEnabled || !locatorQuery.isPending;
  // A document chunk citation always has element ids; a quote without them is a system error, so don't highlight.
  const textToHighlight =
    searchParams.get('highlightText') ??
    (citationRunId && locatorElementIds.length === 0 ? null : (locatorQuery.data?.quote ?? null));
  const shouldHighlight = searchParams.get('highlight');
  const scrollContent = useScrollContent();

  useEffect(() => {
    refContentReady.current = false;
    refLastScrollKey.current = null;
  }, [editor]);

  useEffect(() => {
    const syncScrollToQuery = (afterContentSet = false) => {
      if (!citationLocatorReady) return;
      const hasScrollTarget = Boolean(elemToScroll || locatorElementIds.length || textToHighlight);
      const scrollKey = hasScrollTarget
        ? `${elemToScroll ?? ''}|${JSON.stringify(locatorElementIds)}|${textToHighlight ?? ''}|${shouldHighlight ?? ''}`
        : null;
      // The same target asked for twice is a second request, and the document may have moved to
      // another part in between - so the target alone cannot decide it.
      const askedAgain = askedByClick && navigationKey !== refLastNavigationKey.current;
      refLastNavigationKey.current = navigationKey;
      if (scrollKey === refLastScrollKey.current && !askedAgain) return;
      if (!editor || !refContentNode.current) return;

      if (!hasScrollTarget) {
        scrollContent({ clearHighlight: true, noFocus: true });
        refLastScrollKey.current = null;
        return;
      }

      scrollContent({
        // Reset any previous highlight on every new citation/section, even one with no quote to show.
        clearHighlight: true,
        value: elemToScroll ?? undefined,
        values: locatorElementIds,
        highlightText: textToHighlight ?? undefined,
        shouldHighlight: shouldHighlight ? shouldHighlight === 'true' : undefined,
        // "Edit draft" reloads the draft: keep the user's place, but drop the highlight from reading.
        noHighlight: afterContentSet && requestEdit,
      });
      refLastScrollKey.current = scrollKey;
    };

    const handleEditorContentSet = () => {
      refContentReady.current = true;
      // useOptions skips this flip when the status lands before the editor exists, and never retries.
      editor?.commands?.bumpLiteratureCitationStatusNonce?.();
      // A part swap sets content too, but it is not NEW content.
      if (isPartSwap()) return;
      // The editor is still hidden and out of flow on a first load, so measuring now uses the wrong layout.
      whenSwapDone(() => {
        refLastScrollKey.current = null; // new content (load / version switch) re-applies the scroll + highlight
        syncScrollToQuery(true);
      });
    };

    window.addEventListener('editorContentSet', handleEditorContentSet);
    if (refContentReady.current) syncScrollToQuery();

    return () => {
      window.removeEventListener('editorContentSet', handleEditorContentSet);
    };
  }, [
    scrollContent,
    elemToScroll,
    locatorElementIds,
    citationLocatorReady,
    textToHighlight,
    shouldHighlight,
    askedByClick,
    navigationKey,
    requestEdit,
    editor,
    refContentNode,
  ]);

  const clickCount = useRef(0);
  const boundToastShown = useRef(false);

  useEffect(() => {
    if (isEdit) clickCount.current = TOAST_MAX_CLICK_COUNT;
  }, [isEdit]);

  const classes = useStyles();

  const handleContentClick = () => {
    if (!isEdit && clickCount.current < TOAST_MAX_CLICK_COUNT && canEdit) {
      clickCount.current++;
      const msg = replica
        ? `${TERMINOLOGY.synchronized.upper} documents can't be edited. Click "View ${TERMINOLOGY.master.lower}" on the left.`
        : `Click "${editActionLabel ?? `${doc.draft ? 'Edit' : 'Create'} draft`}" on the left to begin editing.`;
      show('info', msg, { className: classes.toast, timeout: TOAST_TIMEOUT });
    }
    if (
      isEdit &&
      editor &&
      !boundToastShown.current &&
      isBoundDocument(editor.state) &&
      !isSelectionInField(editor.state)
    ) {
      boundToastShown.current = true;
      show('info', 'This content is locked. You can only edit inside template fields.', {
        className: classes.toast,
        timeout: TOAST_TIMEOUT,
      });
    }
  };

  const isSpreadsheet = doc.mock?.isSpreadsheet;
  const hasLandscapeSections = useHasLandscapeSections();
  const { containerWidth, paperWidth, centeringMargin, landscape } = getDocumentConfig(version?.config, {
    isSpreadsheet,
    hasLandscapeSections,
  });

  const scrollContainerWidth = isSpreadsheet ? '100%' : containerWidth;
  const showEditorContent = ['LOADED', 'BLOCKED'].includes(contentStatus);
  const handleEditorContentClick = useHandleEditorContentClick();

  return (
    <CustomScrollbar
      ref={refContentNode as any}
      className={classNames('h-full relative', isSpreadsheet && 'sheet-container')}
      style={{
        width: scrollContainerWidth,
        // The table keeps its intrinsic width inside the scrollbar; the scrollbar must shrink beside a sidebar.
        minWidth: isSpreadsheet ? 0 : scrollContainerWidth,
        transition: layoutTransition('width'),
      }}
      withHorizontal={isSpreadsheet}
    >
      <div id="dummy-bubble-menu" />

      {/* Paper: portrait (210mm centered) or landscape (297mm with scrollbar margin) */}
      <div
        className={classNames(
          'sheet-scrollable',
          'bg-white mt-4 rounded-t-md border-neutral-300 border-t border-l border-r px-[18mm] pt-[14mm]',
          { 'min-h-screen': !doc.isBlock },
          'flex flex-col'
        )}
        style={{
          width: paperWidth,
          marginLeft: centeringMargin,
          marginRight: centeringMargin,
        }}
        onClick={handleContentClick}
      >
        <LinkNodeBubbleMenu />
        <TableNodeBubbleMenu />
        <ImageNodeBubbleMenu />
        <InlineBlockNodeBubbleMenu />
        {/* Composite literature feature gate, matches Insert.tsx entry point. */}
        {organization.featureSettings?.literatures?.enabled && !!organization.folders.literature && (
          <LiteratureCitationBubbleMenu />
        )}
        <DropdownFieldBubbleMenu />
        {usesFormFields(doc) && (
          <>
            <FieldSettingsBubbleMenu />
            <FieldDescriptionTooltip />
            <FormFieldGutter />
          </>
        )}

        {viewChanges && <Changes />}

        <div
          ref={refEditorContent}
          onClick={handleEditorContentClick}
          className={!viewChanges ? 'document-content-container flex-1' : 'flex-1'}
          style={
            viewChanges
              ? { visibility: 'hidden', height: 0, maxHeight: 0, overflow: 'hidden', width: 0 }
              : { width: '100%' }
          }
        >
          <StatusLabel
            data={showEditorContent}
            error={contentStatus === 'ERROR'}
            loadingMessage="Loading document content..."
            errorMessage="Failed to load document content."
          >
            <div
              translate="no"
              className={classNames(
                'notranslate document-content edit-content',
                `custom-${organization.stylesConfig.styleVariant}`,
                { 'landscape-mode': landscape },
                { 'template-detached': isVersionDetached(version) },
                { 'template-bound': isVersionBound(version) },
                { 'template-form-fields': usesFormFields(doc) },
                // The static halves draw the same edit-mode guides as the editor, or the two
                // renderings of one table differ in height and the page moves as a part loads.
                { 'edit-mode': isEdit }
              )}
              style={isEdit ? { overflow: 'unset' } : {}}
            >
              {/* A large document puts one part in the editor; the rest renders as static html here.
                  Inside this wrapper on purpose - it is what gives the static halves the edit-mode
                  treatments that have no static equivalent, the page-break label above all. */}
              <StaticElements block="above" landscape={landscape} />
              <EditorContent editor={editor} />
              <StaticElements block="below" landscape={landscape} />
            </div>
          </StatusLabel>
        </div>
      </div>

      {/* Comments column - positioned at right edge */}
      <div
        className="w-[16mm] h-full comment-icons-container"
        style={{
          position: 'absolute',
          top: 'calc(16px + 14mm)',
          right: '1mm',
        }}
      >
        <div style={{ visibility: viewChanges ? 'hidden' : 'visible' }}>
          {showEditorContent && <EditorCommentsContainer refEditorContent={refEditorContent} />}
        </div>
      </div>

      {/* Footer (matches paper styling) */}
      <div
        className="rounded-b-md bg-white border-neutral-300 border-b border-l border-r mb-6 dummy-footer"
        style={{
          height: doc.isBlock ? '14mm' : '18mm',
          width: paperWidth,
          marginLeft: centeringMargin,
          marginRight: centeringMargin,
        }}
      >
        <div
          style={{ height: '100%', marginLeft: '18mm', marginRight: '18mm', background: 'transparent' }}
          onClick={handleEditorContentClick}
        />
      </div>
    </CustomScrollbar>
  );
};

export default Content;
